import React, { useEffect, useState, useRef } from "react";
import Layout from "../../../layouts/default";
import {
    Card,
    Row,
    Col,
    Button,
    Table,
    Form,
    Alert,
    Spinner,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import JsonModal from "../EmailCampaignRunReport/JsonModel";
import SmartPagination from "../EmailCampaignRunReport/DynamicSmartPagination";
import {
    getCampaignInfo,
    getSmsCampaignDataIssueSummary,
    getSmsCampaignValidContacts,
} from "../../../store/slices/smsCampaign";
import SMSCampaignDataIssueSummary from "./smsDataIssueSummary";

const logsPerPage = 10;

const CampaignRunReportByUuid = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { campaignUuid } = useParams();

    const smsCampaign = useSelector((s) => s.smsCampaignReducer);

    const campaignInfo = smsCampaign?.campaignInfo?.data;
    const campaignInfoLoading = smsCampaign?.campaignInfo?.loading;
    const summaryLoading = smsCampaign?.dataIssueSummary?.loading;

    const [page, setPage] = useState(1);
    const [filterMobile, setFilterMobile] = useState("");
    const [appliedMobileFilter, setAppliedMobileFilter] = useState("");

    const [records, setRecords] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
        total: 0,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedPayload, setSelectedPayload] = useState(null);

    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => (mountedRef.current = false);
    }, []);

    useEffect(() => {
        if (!campaignUuid) return;
        dispatch(getCampaignInfo({ campaignUuid }));
        dispatch(getSmsCampaignDataIssueSummary({ campaignUuid }));
    }, [campaignUuid]);

    const fetchValidMobileLogs = async () => {
        if (!campaignUuid) return;

        setLoading(true);
        setError(null);

        try {
            const payload = {
                campaignUuid,
                page,
                limit: logsPerPage,
                mobile: appliedMobileFilter || undefined,
            };

            const res = await dispatch(getSmsCampaignValidContacts(payload));
            const responseData = res?.payload?.data || {};

            const recordsArr = responseData?.records || [];
            const paginationData = responseData?.pagination || {};

            if (!mountedRef.current) return;

            setRecords(recordsArr);
            setPagination({
                page: paginationData.page,
                limit: paginationData.limit,
                totalPages: paginationData.totalPages,
                hasNextPage: paginationData.hasNextPage,
                hasPrevPage: paginationData.hasPrevPage,
                total: responseData?.total || 0,
            });

            if (recordsArr.length === 0) {
                setError("No records found");
            }
        } catch (err) {
            if (mountedRef.current) setError("Failed to load records");
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        fetchValidMobileLogs();
    }, [campaignUuid, page, appliedMobileFilter]);

    const handleFilter = () => {
        setPage(1);
        setAppliedMobileFilter(filterMobile.trim());
    };

    const handleReset = () => {
        setFilterMobile("");
        setAppliedMobileFilter("");
        setPage(1);
    };

    const handleBack = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate("/campaign/sms-campaign-report");
    };

    return (
        <Layout>
            <div>
                <Button variant="secondary" className="mb-3" onClick={handleBack}>
                    ← Back
                </Button>

                <h3 className="mb-4">SMS Campaign Run Report</h3>

                {campaignInfoLoading || summaryLoading ? (
                    <div className="text-center my-5">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <>
                        {campaignInfo && (
                            <Card className="mb-4 shadow border-0">
                                <Card.Header className="d-flex justify-content-between align-items-center py-3 px-4">
                                    <h5 className="mb-0 fw-bold text-dark">Campaign Summary</h5>

                                    <span
                                        className="badge rounded-pill px-3 py-2"
                                        style={{
                                            fontSize: "0.85rem",
                                            backgroundColor:
                                                campaignInfo?.status === "completed"
                                                    ? "#198754"
                                                    : campaignInfo?.status === "processing"
                                                        ? "#0d6efd"
                                                        : campaignInfo?.status === "pending"
                                                            ? "#ffc107"
                                                            : "#6c757d",
                                            color: campaignInfo?.status === "pending" ? "#212529" : "#fff",
                                        }}
                                    >
                                        {campaignInfo?.status || "NA"}
                                    </span>
                                </Card.Header>

                                <Card.Body className="pt-4 pb-3 px-4">
                                    <Row className="mb-4 gy-3">
                                        <Col md={6}>
                                            <div className="p-3 rounded bg-light border">
                                                <div className="text-muted small">Campaign Name</div>
                                                <div className="fw-semibold fs-6">
                                                    {campaignInfo?.campaignName || "NA"}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="p-3 rounded bg-light border">
                                                <div className="text-muted small">Campaign UUID</div>
                                                <div className="fw-semibold fs-6">
                                                    {campaignInfo?.campaignUuid || "NA"}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row className="mb-4 gy-3">
                                        <Col md={6}>
                                            <div className="p-3 rounded bg-light border">
                                                <div className="text-muted small">Trigger Time</div>
                                                <div className="fw-semibold fs-6">
                                                    {(() => {
                                                        const raw = campaignInfo?.scheduledTime;
                                                        let dt = DateTime.fromISO(raw);
                                                        if (!dt.isValid)
                                                            dt = DateTime.fromFormat(raw, "yyyy-MM-dd H:m:s");
                                                        return dt.isValid
                                                            ? dt.toFormat("dd MMM yyyy, hh:mm a")
                                                            : "NA";
                                                    })()}
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md={6}>
                                            <div className="p-3 rounded bg-light border">
                                                <div className="text-muted small">Channel</div>
                                                <div className="fw-semibold fs-6 text-primary">SMS</div>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row className="gy-3">
                                         <Col md={3}>
                                            <div className="p-3 rounded bg-white border h-100">
                                                <div className="text-muted small">Total Audience Size</div>
                                                <div className="fw-semibold fs-6 text-success">
                                                    {campaignInfo?.totalAudienceCount ?? "0"}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="p-3 rounded bg-white border h-100">
                                                <div className="text-muted small">Total Mobile Numbers</div>
                                                <div className="fw-semibold fs-6 text-success">
                                                    {campaignInfo?.totalMobilesCount ?? "0"}
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md={3}>
                                            <div className="p-3 rounded bg-white border h-100">
                                                <div className="text-muted small">Valid Mobiles</div>
                                                <div className="fw-semibold fs-6 text-success">
                                                    {campaignInfo?.totalValidMobiles ?? "0"}
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md={3}>
                                            <div className="p-3 rounded bg-white border h-100">
                                                <div className="text-muted small">Invalid Mobiles</div>
                                                <div className="fw-semibold fs-6 text-danger">
                                                    {(campaignInfo?.totalMobilesCount ?? 0) -
                                                        (campaignInfo?.totalValidMobiles ?? 0)}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}

                        <SMSCampaignDataIssueSummary campaignData={campaignInfo} />

                        <Card className="mb-3 shadow-sm">
                            <Card.Body>
                                <Row>
                                    <Col md={4}>
                                        <Form.Label>Mobile Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter mobile"
                                            value={filterMobile}
                                            onChange={(e) =>
                                                setFilterMobile(e.target.value.replace(/\D/g, ""))
                                            }
                                        />
                                    </Col>

                                    <Col md={4} className="d-flex gap-2 align-items-end">
                                        <Button variant="primary" onClick={handleFilter}>
                                            Filter
                                        </Button>
                                        <Button variant="outline-secondary" onClick={handleReset}>
                                            Reset
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {loading ? (
                            <div className="text-center my-5">
                                <Spinner animation="border" />
                            </div>
                        ) : (
                            <>
                                {error && (
                                    <Alert variant="danger" className="text-center">
                                        {error}
                                    </Alert>
                                )}

                                {records.length > 0 ? (
                                    <>
                                        <Table striped bordered hover>
                                            <thead>
                                                <tr>
                                                    <th>S.No</th>
                                                    <th>Mobile Number</th>
                                                    <th>Timestamp</th>
                                                    <th>JSON</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {records.map((log, idx) => (
                                                    <tr key={log._id || idx}>
                                                        <td>{(page - 1) * logsPerPage + idx + 1}</td>
                                                        <td>{log?.mobileNumber || log?.mobile || "NA"}</td>
                                                        <td>
                                                            {log.timestamp
                                                                ? new Date(log.timestamp).toLocaleString()
                                                                : "NA"}
                                                        </td>
                                                        <td>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-primary"
                                                                onClick={() => {
                                                                    setSelectedPayload(log);
                                                                    setShowModal(true);
                                                                }}
                                                            >
                                                                JSON
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>

                                        <SmartPagination
                                            totalPages={pagination.totalPages}
                                            currentPage={pagination.page}
                                            onPageChange={setPage}
                                        />
                                    </>
                                ) : (
                                    !error && (
                                        <div className="text-center text-muted my-4">
                                            No records found.
                                        </div>
                                    )
                                )}
                            </>
                        )}
                    </>
                )}
            </div>

            <JsonModal
                show={showModal}
                onClose={() => setShowModal(false)}
                payload={selectedPayload}
            />
        </Layout>
    );
};

export default CampaignRunReportByUuid;
